import { useCallback, useState, type CSSProperties } from 'react';
import { useDismiss } from './Menu';

/**
 * Select.dc.html, verbatim. Every Select is searchable (product rule): a
 * type-to-filter row at the top (32px, search icon, "Type to filter"), a
 * "No matches" empty state, and an optional mono `hint` under an option
 * (SNOMED CT / DICOM codes). A `disabled` option is listed greyed and cannot
 * be chosen (a Type the file cannot be). Trigger looks like an input; open
 * state draws an ink border + 1px ring. Sizes: sm 30/12.5, md 36/13, lg 38/14.
 */
export interface SelectOption {
  value: string;
  label: string;
  hint?: string;
  disabled?: boolean;
}

export interface SelectProps {
  value: string;
  options: ReadonlyArray<string | SelectOption>;
  onChange: (value: string) => void;
  placeholder?: string;
  size?: 'sm' | 'md' | 'lg';
  width?: number | string;
  disabled?: boolean;
  /** default true; the product rule says every Select is searchable */
  searchable?: boolean;
  style?: CSSProperties;
}

const DIMS = { sm: [30, 12.5, 8], md: [36, 13, 10], lg: [38, 14, 10] } as const;

export function Select({ value, options, onChange, placeholder = 'Select…', size = 'md', width = 'auto', disabled, searchable = true, style }: SelectProps) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const close = useCallback(() => {
    setOpen(false);
    setQ('');
  }, []);
  useDismiss(open, close);

  const opts: SelectOption[] = options.map((o) => (typeof o === 'string' ? { value: o, label: o } : o));
  const cur = opts.find((o) => o.value === value);
  const [h, fs, padL] = DIMS[size];
  const ql = q.toLowerCase();
  const visible = searchable && ql ? opts.filter((o) => (o.label + ' ' + (o.hint || '')).toLowerCase().includes(ql)) : opts;
  const isOpen = open && !disabled;

  return (
    <div
      style={{ position: 'relative', width, fontFamily: "'Instrument Sans', system-ui, sans-serif", ...style }}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        if (e.key === 'Escape') close();
        if ((e.key === 'Enter' || e.key === ' ') && !disabled && (e.target as HTMLElement).tagName !== 'INPUT') {
          e.preventDefault();
          setOpen(!open);
        }
      }}
    >
      <div
        tabIndex={0}
        role="button"
        className="tr-hover-soft-fg"
        onClick={() => {
          if (!disabled) {
            setOpen(!open);
            setQ('');
          }
        }}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          height: h,
          padding: `0 8px 0 ${padL}px`,
          border: `1px solid ${isOpen ? '#0f0f10' : '#e3e3e1'}`,
          borderRadius: 7,
          background: disabled ? '#fafaf9' : '#fff',
          color: disabled ? '#8a8a8f' : cur ? '#0f0f10' : '#8a8a8f',
          fontSize: fs,
          cursor: disabled ? 'default' : 'pointer',
          outline: 'none',
          boxShadow: isOpen ? '0 0 0 1px #0f0f10' : 'none',
          userSelect: 'none',
        }}
      >
        <span className="tr-ellipsis">{cur ? cur.label : placeholder}</span>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#6b6b70" strokeWidth="2" style={{ flex: 'none' }} aria-hidden>
          <path d="m6 9 6 6 6-6" />
        </svg>
      </div>
      {isOpen ? (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            minWidth: '100%',
            width: 'max-content',
            maxWidth: 360,
            padding: 4,
            background: '#fff',
            border: '1px solid #e3e3e1',
            borderRadius: 8,
            boxShadow: '0 8px 24px rgba(0,0,0,.1)',
            zIndex: 40,
          }}
        >
          {searchable ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, height: 32, padding: '0 8px', marginBottom: 4, borderBottom: '1px solid #f0f0ee', color: '#8a8a8f' }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                <circle cx="11" cy="11" r="7" />
                <path d="m20 20-3.5-3.5" />
              </svg>
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                autoFocus
                placeholder="Type to filter"
                style={{ flex: 1, minWidth: 0, border: 0, outline: 0, background: 'transparent', fontSize: 13, color: '#0f0f10', fontFamily: 'inherit' }}
              />
            </div>
          ) : null}
          {searchable && visible.length === 0 ? <div style={{ padding: 10, fontSize: 12.5, color: '#8a8a8f' }}>No matches</div> : null}
          <div style={{ maxHeight: 260, overflow: 'auto' }}>
            {visible
              .filter((o) => o.value !== '' || !cur)
              .map((o) => {
                const selected = o.value === value;
                return (
                  <div
                    key={o.value}
                    className={o.disabled ? undefined : 'tr-hover-track'}
                    aria-disabled={o.disabled || undefined}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (o.disabled) return;
                      close();
                      onChange(o.value);
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 12,
                      padding: '7px 10px',
                      borderRadius: 5,
                      fontSize: 13,
                      cursor: o.disabled ? 'default' : 'pointer',
                      background: selected ? '#f6f6f4' : 'transparent',
                      color: o.disabled ? '#b8b8bb' : o.value === '' ? '#8a8a8f' : '#0f0f10',
                      fontWeight: selected ? 500 : 400,
                    }}
                  >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
                      <span style={{ whiteSpace: 'nowrap' }}>{o.label}</span>
                      {o.hint ? <span style={{ fontSize: 11, color: '#8a8a8f', fontFamily: "'JetBrains Mono', monospace" }}>{o.hint}</span> : null}
                    </div>
                    {selected ? (
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" style={{ flex: 'none' }} aria-hidden>
                        <path d="m5 12 5 5L20 7" />
                      </svg>
                    ) : null}
                  </div>
                );
              })}
          </div>
        </div>
      ) : null}
    </div>
  );
}
